import React from 'react';
import Stack from '@mui/material/Stack';
import Container from 'components/Layout/Container';
import clxs from 'classnames';
import { Link as RouterLink } from 'react-router-dom';
import SendIcon from '@mui/icons-material/ArrowForward';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
// @ts-ignore
import { MotionAnimate } from 'react-motion-animate';
import Box from '@mui/material/Box';
import { shopifyCDNFile } from 'lib/shopifyImageSizer';

interface TwoCardAreaProps {
  cardOneSrc?: string;
  cardTwoSrc?: string;
}

const useStyles = (props: TwoCardAreaProps) =>
  makeStyles(
    (theme: Theme) => ({
      root: {
        width: '100%',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 100%',
        position: 'relative',
        overflow: 'hidden',
        [theme.breakpoints.down('md')]: {
          backgroundSize: 'auto 100%',
        },
      },
      container: {
        padding: '80px 20px',
        [theme.breakpoints.down('lg')]: {
          padding: '40px 10px',
        },
      },
      background: {
        opacity: 0.6,
        position: 'absolute',
        left: 0,
        top: 0,
        width: '100%',
        height: '100%',
        zIndex: -1,
        objectFit: 'cover',
      },
      title: {
        paddingBottom: theme.spacing(1),
        [theme.breakpoints.down('md')]: {
          paddingBottom: theme.spacing(0.5),
        },
      },
      center: {
        textAlign: 'center',
      },
      section: {
        marginTop: theme.spacing(4),
        width: '100%',
        maxWidth: 600,
      },
      card: {
        maxWidth: 400,
        maxHeight: 256,
        borderRadius: 10,
        width: '100%',
        display: 'inline-block',
        alignSelf: 'flex-end',
        transition: 'background-image 3s',
        backgroundPosition: 'center',
        backgroundSize: 'cover',
        backgroundRepeat: 'no-repeat',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          float: 'left',
          paddingTop: '63.08%',
          content: "''",
        },
        '&::after': {
          display: 'block',
          content: "''",
          clear: 'both',
        },
        [theme.breakpoints.down('md')]: {
          maxWidth: '63%',
        },
      },
      card1: {
        backgroundImage: 'url(' + shopifyCDNFile(props.cardOneSrc) + ')',
      },
      card2: {
        backgroundImage: 'url(' + shopifyCDNFile(props.cardTwoSrc) + ')',
      },
      cardContainer1: {
        left: 0,
      },
      cardContainer: {
        position: 'absolute',
        top: 0,
        height: 400,
        maxWidth: 400,
        width: '100%',
        [theme.breakpoints.down('md')]: {
          height: 'calc((100vw - 20px) * 0.5625)',
        },
      },
      cardContainer2: {
        right: 0,
        display: 'flex',
        flexDirection: 'column-reverse',
      },
      button: {
        marginTop: theme.spacing(4),
      },
      cardArea: {
        width: '100%',
        maxWidth: 600,
        height: 400,
        position: 'relative',
        marginTop: theme.spacing(4),
        [theme.breakpoints.down('md')]: {
          height: 'calc((100vw - 20px) * 0.5625)',
        },
        '& > div:first-child > div[style="transform: none;"] > div > div': {
          backgroundImage: 'url(' + shopifyCDNFile(props.cardOneSrc) + ')',
        },
        '& > div:nth-child(2) > div[style="transform: none;"] > div > div': {
          backgroundImage: 'url(' + shopifyCDNFile(props.cardTwoSrc) + ')',
        },
      },
    }),
    { name: 'CustomisationSection' },
  );

const TwoCardsArea: React.FC<TwoCardAreaProps> = ({ cardOneSrc, cardTwoSrc }) => {
  const classes = useStyles({
    cardOneSrc,
    cardTwoSrc,
  })();

  return (
    <div className={classes.cardArea}>
      <MotionAnimate animation="scrollPosition" xPos={[window.innerWidth / 6, 0]} scrollPositions={[0, 0.7]}>
        <div className={clxs(classes.cardContainer, classes.cardContainer2)}>
          <div className={clxs(classes.card, classes.card1)} />
        </div>
      </MotionAnimate>
      <MotionAnimate animation="scrollPosition" xPos={[window.innerWidth / -6, 0]} scrollPositions={[0, 0.7]}>
        <div className={clxs(classes.cardContainer, classes.cardContainer1)}>
          <div className={clxs(classes.card, classes.card2)} />
        </div>
      </MotionAnimate>
    </div>
  );
};

interface CustomisationSectionProps {
  bgSrc?: string;
  title?: string;
  description?: string;
  link?: string;
  linkTitle?: string;
  cardOneSrc?: string;
  cardTwoSrc?: string;
}

const CustomisationSection: React.FC<CustomisationSectionProps> = ({
  title = 'Create a custom gift card. Eftpos or Mastercard. Physical or Digital.',
  description = 'Go ahead and personalise your card by adding your logo and a message, or even choose from one of our unique designs.',
  link = '/corporate/mastercard',
  linkTitle = 'Select a Card',
  bgSrc,
  cardOneSrc,
  cardTwoSrc,
}) => {
  const classes = useStyles({})();
  return (
    <div className={classes.root}>
      {bgSrc && <img src={shopifyCDNFile(bgSrc)} className={classes.background} alt={title} />}
      <Container sx={{ flexGrow: 1 }} className={classes.container}>
        <Stack alignItems="center">
          <Box maxWidth="600px">
            <Typography variant="h1" className={clxs(classes.title, classes.center)} gutterBottom>
              {title}
            </Typography>
          </Box>
          {cardOneSrc && cardTwoSrc && <TwoCardsArea cardOneSrc={cardOneSrc} cardTwoSrc={cardTwoSrc} />}
          <Box mt={'40px'}>
            <div className={classes.section}>
              <Typography component="div" variant="body1" textAlign="center">
                {description}
              </Typography>
            </div>
            <Box sx={{ textAlign: 'center' }}>
              <Button
                component={RouterLink} // It is a link...
                to={link}
                size="large"
                color="secondary"
                variant="outlined"
                endIcon={<SendIcon />}
                className={classes.button}
              >
                {linkTitle}
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </div>
  );
};

export default CustomisationSection;
